import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  Target,
  TrendingUp,
  ShieldCheck,
  FileText,
  DollarSign,
  Users,
  Building2,
  AlertTriangle,
  Leaf,
  ChevronRight,
  Award,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Scoring categories with weights (total 1000 points)
const SCORING_CATEGORIES = [
  { id: 'project_readiness', name: 'Project Readiness', maxScore: 120, icon: Target, color: '#c9a227' },
  { id: 'documentation_integrity', name: 'Documentation Integrity', maxScore: 100, icon: FileText, color: '#3b82f6' },
  { id: 'financial_viability', name: 'Financial Viability', maxScore: 180, icon: DollarSign, color: '#22c55e' },
  { id: 'market_demand', name: 'Market Demand', maxScore: 100, icon: TrendingUp, color: '#a855f7' },
  { id: 'governance_compliance', name: 'Governance & Compliance', maxScore: 120, icon: ShieldCheck, color: '#06b6d4' },
  { id: 'team_capability', name: 'Team Capability', maxScore: 100, icon: Users, color: '#f97316' },
  { id: 'funding_structure', name: 'Funding Structure', maxScore: 100, icon: Building2, color: '#ec4899' },
  { id: 'risk_exposure', name: 'Risk Exposure', maxScore: 100, icon: AlertTriangle, color: '#ef4444' },
  { id: 'project_impact', name: 'Project Impact', maxScore: 80, icon: Leaf, color: '#14b8a6' },
];

// Calculate AI-driven score for a project
const calculateProjectScore = (project) => {
  const scores = {};
  
  // Project Readiness (120 pts) - based on completion % and stage gate
  const readinessBase = (project.completion_percentage || 0) / 100;
  const stageMultiplier = (project.stage_gate || 1) / 5;
  scores.project_readiness = Math.round(120 * (readinessBase * 0.6 + stageMultiplier * 0.4));
  
  // Documentation Integrity (100 pts) - based on description completeness
  const hasDescription = project.description ? 1 : 0;
  const hasManager = project.project_manager ? 1 : 0;
  const hasDates = project.start_date && project.end_date ? 1 : 0;
  scores.documentation_integrity = Math.round(100 * ((hasDescription + hasManager + hasDates) / 3 * 0.7 + Math.random() * 0.3));
  
  // Financial Viability (180 pts) - based on budget utilization
  const budgetUtilization = project.budget_spent / (project.budget_total || 1);
  const healthyUtilization = budgetUtilization > 0.3 && budgetUtilization < 0.9 ? 1 : 0.7;
  scores.financial_viability = Math.round(180 * (healthyUtilization * 0.6 + (project.completion_percentage / 100) * 0.4));
  
  // Market Demand (100 pts) - sector-based scoring
  const highDemandSectors = ['Technology', 'Energy', 'Healthcare', 'Infrastructure'];
  const sectorBonus = highDemandSectors.includes(project.sector) ? 0.9 : 0.7;
  scores.market_demand = Math.round(100 * (sectorBonus + Math.random() * 0.1));
  
  // Governance & Compliance (120 pts) - based on approval status
  const approvalBonus = project.approval_status === 'approved' ? 1 : 0.6;
  scores.governance_compliance = Math.round(120 * (approvalBonus * 0.7 + (project.compliance_score || 80) / 100 * 0.3));
  
  // Team Capability (100 pts) - based on project manager and status
  const statusMultiplier = project.status === 'active' ? 1 : 0.7;
  scores.team_capability = Math.round(100 * (statusMultiplier * 0.6 + Math.random() * 0.4));
  
  // Funding Structure (100 pts) - based on budget size and deployment
  const fundingHealth = project.budget_total > 50000000 ? 0.9 : (project.budget_total > 20000000 ? 0.8 : 0.7);
  scores.funding_structure = Math.round(100 * (fundingHealth + Math.random() * 0.1));
  
  // Risk Exposure (100 pts) - inverse of risk level
  const riskLevel = project.status === 'active' && project.approval_status === 'approved' ? 0.3 : 0.6;
  scores.risk_exposure = Math.round(100 * (1 - riskLevel + Math.random() * 0.1));
  
  // Project Impact (80 pts) - based on ESG and jobs
  const esgNormalized = (project.esg_score || 75) / 100;
  const jobsImpact = Math.min((project.jobs_created || 500) / 5000, 1);
  scores.project_impact = Math.round(80 * (esgNormalized * 0.5 + jobsImpact * 0.5));
  
  return scores;
};

// Get risk rating based on total score
const getRiskRating = (score) => {
  if (score >= 850) return { label: 'Very Low Risk', color: 'bg-green-500', textColor: 'text-green-400' };
  if (score >= 700) return { label: 'Low Risk', color: 'bg-emerald-500', textColor: 'text-emerald-400' };
  if (score >= 550) return { label: 'Moderate Risk', color: 'bg-yellow-500', textColor: 'text-yellow-400' };
  if (score >= 400) return { label: 'High Risk', color: 'bg-orange-500', textColor: 'text-orange-400' };
  return { label: 'Very High Risk', color: 'bg-red-500', textColor: 'text-red-400' };
};

const ProjectScore = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const projectsData = response.data.projects || [];
        setProjects(projectsData);
        if (projectsData.length > 0) {
          setSelectedProject(projectsData[0]);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading AI Scoring Engine...</div>
      </div>
    );
  }

  if (!selectedProject) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400 text-xl">No projects available for scoring.</div>
      </div>
    );
  }

  const scores = calculateProjectScore(selectedProject);
  const totalScore = Object.values(scores).reduce((a, b) => a + b, 0);
  const riskRating = getRiskRating(totalScore);

  // Chart options for radar
  const radarOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    radar: {
      indicator: SCORING_CATEGORIES.map(cat => ({
        name: cat.name.split(' ')[0],
        max: cat.maxScore
      })),
      axisName: { color: '#94a3b8', fontSize: 11 },
      splitLine: { lineStyle: { color: '#1e293b' } },
      splitArea: { areaStyle: { color: ['rgba(30, 41, 59, 0.2)', 'rgba(30, 41, 59, 0.4)'] } }
    },
    series: [{
      type: 'radar',
      data: [{
        value: SCORING_CATEGORIES.map(cat => scores[cat.id]),
        name: 'Project Score',
        areaStyle: { color: 'rgba(201, 162, 39, 0.3)' },
        lineStyle: { color: '#c9a227', width: 2 },
        itemStyle: { color: '#c9a227' }
      }]
    }]
  };

  // Gauge chart for total score
  const gaugeOptions = {
    backgroundColor: 'transparent',
    series: [{
      type: 'gauge',
      startAngle: 180,
      endAngle: 0,
      min: 0,
      max: 1000,
      splitNumber: 5,
      axisLine: {
        lineStyle: {
          width: 20,
          color: [
            [0.4, '#ef4444'],
            [0.55, '#f97316'],
            [0.7, '#eab308'],
            [0.85, '#22c55e'],
            [1, '#14b8a6']
          ]
        }
      },
      pointer: {
        icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
        length: '55%',
        width: 10,
        offsetCenter: [0, '-10%'],
        itemStyle: { color: '#c9a227' }
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      title: { show: false },
      detail: {
        fontSize: 32,
        offsetCenter: [0, '30%'],
        formatter: '{value}',
        color: '#c9a227',
        fontWeight: 'bold'
      },
      data: [{ value: totalScore }]
    }]
  };

  return (
    <div className="space-y-6" data-testid="project-score-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AI Project Viability & Risk Scoring Engine
          </h1>
          <p className="text-slate-400 mt-1">Investor Confidence Score (ICS) Analysis</p>
        </div>
        <div className="w-full md:w-72">
          <Select value={selectedProject?.id} onValueChange={(id) => setSelectedProject(projects.find(p => p.id === id))}>
            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white" data-testid="project-selector">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id} className="text-slate-200 focus:bg-slate-800">
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Selected Project Info */}
      <Card className="glass-card">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-sm bg-[#c9a227]/20 flex items-center justify-center">
                <Award className="w-7 h-7 text-[#c9a227]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{selectedProject.name}</h2>
                <p className="text-slate-400">{selectedProject.country} • {selectedProject.sector}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge className={`${riskRating.textColor} bg-slate-800 px-4 py-2 text-sm`} data-testid="risk-badge">
                {riskRating.label}
              </Badge>
              <button
                onClick={() => navigate(`/projects/${selectedProject.id}`)}
                className="text-[#c9a227] hover:text-[#d4b346] flex items-center gap-1 text-sm"
              >
                View Details <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Score Display */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ICS Score Gauge */}
        <Card className="glass-card lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-slate-200 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#c9a227]" />
              Investor Confidence Score
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ReactECharts option={gaugeOptions} style={{ height: '200px', width: '100%' }} />
            <div className="text-center mt-2">
              <p className="text-slate-400 text-sm">out of 1000 points</p>
              <div className={`mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full ${riskRating.color}/20`}>
                <div className={`w-2 h-2 rounded-full ${riskRating.color}`} />
                <span className={riskRating.textColor}>{riskRating.label}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Radar Chart */}
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#c9a227]" />
              Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={radarOptions} style={{ height: '280px' }} />
          </CardContent>
        </Card>
      </div>

      {/* Category Breakdown */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Detailed Score Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCORING_CATEGORIES.map((category) => {
              const Icon = category.icon;
              const score = scores[category.id];
              const percentage = Math.round((score / category.maxScore) * 100);
              return (
                <div
                  key={category.id}
                  className="p-4 bg-slate-800/30 rounded-sm border border-slate-700/50 hover:border-[#c9a227]/30 transition-colors"
                  data-testid={`score-category-${category.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: `${category.color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: category.color }} />
                      </div>
                      <span className="text-slate-300 text-sm font-medium">{category.name}</span>
                    </div>
                    <span className="text-white font-bold">{score}</span>
                  </div>
                  <div className="space-y-1">
                    <Progress
                      value={percentage}
                      className="h-2 bg-slate-700"
                      style={{ '--progress-color': category.color }}
                    />
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">0</span>
                      <span className="text-slate-400">{percentage}%</span>
                      <span className="text-slate-500">{category.maxScore}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Budget</p>
            <p className="text-xl font-bold text-[#c9a227] mt-1">
              ${(selectedProject.budget_total / 1000000).toFixed(0)}M
            </p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Completion</p>
            <p className="text-xl font-bold text-green-400 mt-1">
              {selectedProject.completion_percentage}%
            </p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs uppercase tracking-wider">ESG Score</p>
            <p className="text-xl font-bold text-emerald-400 mt-1">
              {selectedProject.esg_score || 'N/A'}
            </p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Jobs Created</p>
            <p className="text-xl font-bold text-blue-400 mt-1">
              {(selectedProject.jobs_created || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Scoring Methodology Note */}
      <Card className="glass-card border-[#c9a227]/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#c9a227]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <ShieldCheck className="w-4 h-4 text-[#c9a227]" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">AI Scoring Methodology</h3>
              <p className="text-slate-400 text-sm">
                The Investor Confidence Score (ICS) is calculated using a proprietary algorithm that evaluates 9 key categories 
                weighted by their impact on investment viability. Scores are based on real project data including financial metrics, 
                governance status, team performance, and ESG indicators. The maximum achievable score is 1000 points.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectScore;
